// ============================================
// Inlined XSS-safe HTML utilities
// ============================================

export const escapeHtml = (str) => {
    if (typeof str !== 'string') return str;
    return str.replace(/[&<>"']/g, m => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
};

const TRUST_CAP = 5000;
const _trusted = new Map();
export const isTrusted = (val) => _trusted.has(val);
export const markTrusted = (val) => {
    if (_trusted.size >= TRUST_CAP) {
        // Evict oldest half (Map preserves insertion order) instead of
        // clearing everything, so recently created fragments stay trusted.
        const evict = Math.floor(TRUST_CAP / 2);
        let i = 0;
        for (const key of _trusted.keys()) {
            _trusted.delete(key);
            if (++i >= evict) break;
        }
    }
    _trusted.set(val, true);
    return val;
};

export const html = (strings, ...values) => {
    const result = strings.reduce((acc, str, i) => {
        const val = values[i];
        let placeholder = '';
        if (val !== undefined && val !== null) {
            if (isTrusted(val)) {
                placeholder = val;
            } else if (Array.isArray(val)) {
                placeholder = val
                    .filter(v => v !== undefined && v !== null)
                    .map(v => isTrusted(v) ? v : escapeHtml(String(v)))
                    .join('');
            } else if (typeof val === 'string') {
                placeholder = escapeHtml(val);
            } else {
                placeholder = escapeHtml(String(val)); // numbers, booleans
            }
        }
        return acc + str + placeholder;
    }, '');
    return markTrusted(result);
};

// ============================================
// Security guards
// ============================================

// Tags allowed for text({ tag }) — text-level semantics only.
const ALLOWED_TAGS = new Set([
    'span', 'div', 'p', 'label', 'small', 'strong', 'em', 'b', 'i', 'u', 's',
    'mark', 'kbd', 'samp', 'var', 'sub', 'sup', 'abbr', 'cite', 'q',
    'pre', 'blockquote', 'figcaption', 'caption', 'dd', 'dt', 'address'
]);

const _safeTag = (tag, fallback = 'span') => {
    const t = String(tag || fallback).toLowerCase();
    if (!ALLOWED_TAGS.has(t)) {
        console.warn(`[ContentComposer] Tag "${tag}" is not allowed. Falling back to <${fallback}>.`);
        return fallback;
    }
    return t;
};

// Block dangerous URL protocols. Allows http(s), mailto, tel,
// relative paths, anchors, and query strings.
// Shared XSS kernel — ElementBuilder imports this instead of duplicating.
export const SAFE_URL_PATTERN = /^(?:(?:https?|mailto|tel|ftp):|[^a-z0-9+.-]|[a-z0-9+.-]*(?:[^a-z0-9+.:-]|$))/i;

/**
 * @param {string} url
 * @param {string} [source='ContentComposer'] — console warn prefix for callers
 * @returns {string}
 */
export function safeUrl(url, source = 'ContentComposer') {
    const u = String(url ?? '').trim();
    if (u === '') return '#';
    if (!SAFE_URL_PATTERN.test(u)) {
        console.warn(`[${source}] Blocked unsafe URL: "${url}".`);
        return '#';
    }
    return u;
}

// ============================================
// Internal builders
// ============================================

/** Escape style keys and values; skip non-primitive values. */
const _styleString = (style) => {
    return Object.entries(style)
        .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
        .map(([k, v]) => `${escapeHtml(String(k))}:${escapeHtml(String(v))}`)
        .join(';');
};

// Result is marked trusted: every embedded value is already escaped above,
// and the surrounding quotes must survive `html` interpolation intact.
const _attrs = (options = {}) => {
    let result = '';
    if (options.class) result += ` class="${escapeHtml(String(options.class))}"`;
    if (options.style && typeof options.style === 'object') {
        const styleStr = _styleString(options.style);
        if (styleStr) result += ` style="${styleStr}"`;
    }
    return markTrusted(result);
};

/**
 * Resolve rest-param arguments into { items, options }.
 * All args are content parts unless the last is a plain options object.
 */
const _resolveArgs = (args) => {
    if (args.length === 0) return { items: [''], options: {} };
    const last = args[args.length - 1];
    if (typeof last === 'object' && last !== null && !isTrusted(last) && !Array.isArray(last)) {
        return { items: args.slice(0, -1), options: last };
    }
    // Single array arg: treat its elements as the content parts
    if (args.length === 1 && Array.isArray(args[0])) {
        return { items: args[0], options: {} };
    }
    return { items: args, options: {} };
};

// ============================================
// Public helpers
// ============================================

/**
 * Styled text element (default <span>).
 *   text('hello')
 *   text('hello', { style: { color: 'red' } })
 *   text(bold('x'), ' and ', italic('y'))
 *   text('block', { tag: 'p' })   // tag must be in the allowlist
 */
export const text = (...args) => {
    const { items, options } = _resolveArgs(args);
    const tag = _safeTag(options.tag);
    return html`<${tag}${_attrs(options)}>${items}</${tag}>`;
};

/**
 * Heading element (h1-h6). Level clamped to 1-6.
 *   header(3, 'Title')
 *   header(1, 'Section: ', code('init'), { style: { color: 'blue' } })
 */
export const header = (level, ...args) => {
    const n = Number(level);
    const tag = `h${Math.min(Math.max(1, Number.isFinite(n) ? Math.round(n) : 1), 6)}`;
    const { items, options } = _resolveArgs(args);
    return html`<${tag}${_attrs(options)}>${items}</${tag}>`;
};

/** Ordered list <ol>. Items may be strings or trusted fragments (lists nest naturally). */
export const ordered = (items = [], options = {}) => {
    const itemsHtml = (items || []).map(item => html`<li>${item}</li>`);
    return html`<ol${_attrs(options)}>${itemsHtml}</ol>`;
};

/** Unordered list <ul>. Items may be strings or trusted fragments (lists nest naturally). */
export const unordered = (items = [], options = {}) => {
    const itemsHtml = (items || []).map(item => html`<li>${item}</li>`);
    return html`<ul${_attrs(options)}>${itemsHtml}</ul>`;
};

/** <strong> element. Rest-param mixed content. */
export const bold = (...args) => {
    const { items, options } = _resolveArgs(args);
    return html`<strong${_attrs(options)}>${items}</strong>`;
};

/** <em> element. Rest-param mixed content. */
export const italic = (...args) => {
    const { items, options } = _resolveArgs(args);
    return html`<em${_attrs(options)}>${items}</em>`;
};

/** <code> element. Rest-param mixed content. */
export const code = (...args) => {
    const { items, options } = _resolveArgs(args);
    return html`<code${_attrs(options)}>${items}</code>`;
};

/**
 * Safe anchor element. href is protocol-validated:
 * javascript:, data:, vbscript: etc. are replaced with "#" and warned.
 *   link('Open docs', 'https://example.com')
 *   link('Visit', '/path', { newTab: true })
 */
export const link = (content, href, options = {}) => {
    const url = safeUrl(href);
    // Static attribute markup — trusted so its quotes survive interpolation.
    const target = options.newTab
        ? markTrusted(' target="_blank" rel="noopener noreferrer"')
        : '';
    return html`<a href="${url}"${target}${_attrs(options)}>${content}</a>`;
};

/** Line break <br>. */
export const br = () => html`<br>`;

/** Horizontal divider <hr>. */
export const divider = (options = {}) => html`<hr${_attrs(options)}>`;
